<template>
  <BaseInnerLoading v-if="isLoading" />

  <div v-else-if="paymentRequest" class="space-y-6">
    <div class="flex items-center justify-center">
      <span class="text-3xl font-semibold">
        {{
          formatToCurrencyString(paymentRequest.amountGross)
        }}
      </span>
    </div>

    <div>
      <div class="mb-2 text-sm text-background-fg/70">
        Customer
      </div>
      <BaseCard
        class="flex flex-wrap items-center justify-between"
      >
        <div class="flex-1">
          <p class="text-lg font-semibold">
            {{ paymentRequest.customer.name }}
          </p>
          <p
            v-if="paymentRequest.customer.email"
            class="flex items-center text-sm text-background-fg/87"
          >
            <IconHeroiconsEnvelope class="mr-2" />

            {{ paymentRequest.customer.email }}
          </p>
          <p
            v-if="paymentRequest.customer.phone"
            class="flex items-center text-sm text-background-fg/87"
          >
            <IconHeroiconsPhone class="mr-2" />
            {{ paymentRequest.customer.phone }}
          </p>
        </div>
      </BaseCard>
    </div>

    <div class="space-y-2 text-sm">
      <div
        v-if="
          paymentRequest.user ||
          paymentRequest.customerCreated
        "
        class="flex flex-wrap justify-between space-x-4"
      >
        <div class="text-background-fg/70">
          Created By
        </div>
        <div class="text-right">
          {{
            paymentRequest.customerCreated
              ? "Customer"
              : paymentRequest.user
          }}
        </div>
      </div>

      <div class="flex flex-wrap justify-between space-x-4">
        <div class="text-background-fg/70"> Status </div>
        <div class="text-right">
          <PaymentStatusChip
            :status="paymentRequest.status"
          />
        </div>
      </div>

      <div class="flex flex-wrap justify-between space-x-4">
        <div class="text-background-fg/70">
          Description
        </div>
        <div class="text-right">
          {{ paymentRequest.description }}
        </div>
      </div>
      <div class="flex flex-wrap justify-between space-x-4">
        <div class="text-background-fg/70">
          Created At
        </div>
        <div class="text-right">
          {{ formatToDate(paymentRequest.createdAt) }} at
          {{ formatToTime(paymentRequest.createdAt) }}
        </div>
      </div>

      <div class="flex flex-wrap justify-between space-x-4">
        <div class="text-background-fg/70">
          Last Sent At
        </div>
        <div class="text-right">
          <template v-if="paymentRequest.lastSentAt">
            {{ formatToDate(paymentRequest.lastSentAt) }} at
            {{ formatToTime(paymentRequest.lastSentAt) }}
          </template>
          <template v-else> - </template>
        </div>
      </div>

      <div
        v-if="paymentRequest.amountVat"
        class="flex flex-wrap justify-between space-x-4"
      >
        <div class="text-background-fg/70"> VAT </div>
        <div class="text-right">
          {{
            formatToCurrencyString(paymentRequest.amountVat)
          }}
        </div>
      </div>
    </div>

    <div
      v-if="
        paymentRequest.status ===
        PAYMENT_REQUEST_STATUS.requested
      "
      class="grid items-center gap-2"
    >
      <ButtonPrimary
        :is-loading="isActioning"
        @click="onResend"
      >
        Resend Request
      </ButtonPrimary>

      <ButtonSecondary
        :is-loading="isActioning"
        @click="onCancel"
      >
        Cancel Request
      </ButtonSecondary>
    </div>
  </div>

  <BaseConfirmation
    ref="cancelPaymentRequestConfirmationRef"
    title="Cancel Payment Request?"
    confirm-label="Yes"
    cancel-label="Go Back"
  />
</template>

<script setup lang="ts">
import type BaseConfirmation from "@/components/BaseConfirmation.vue"
import { PAYMENT_REQUEST_STATUS } from "@/services/paymentRequests/paymentRequest"

const props = defineProps<{
  paymentRequestId: string
}>()

const { data: paymentRequest, isLoading } =
  usePaymentRequest(toRef(props, "paymentRequestId"))

const isActioning = ref(false)

const onCancel = async () => {
  if (isActioning.value) return
  if (cancelPaymentRequestConfirmationRef.value === null)
    return

  const { isCanceled } =
    await cancelPaymentRequestConfirmationRef.value.reveal()

  if (isCanceled) return

  isActioning.value = true
  try {
    await cancelPaymentRequest(props.paymentRequestId)
  } catch {
    toast.error("Failed to cancel payment request")
  } finally {
    isActioning.value = false
  }
}

const onResend = async () => {
  if (isActioning.value) return

  isActioning.value = true
  try {
    await resendPaymentRequest(props.paymentRequestId)
  } catch {
    toast.error("Failed to resend payment request")
  } finally {
    isActioning.value = false
  }
}

const cancelPaymentRequestConfirmationRef = useTemplateRef(
  "cancelPaymentRequestConfirmationRef"
)
</script>
